import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ListingCard, IconSpinner, NamedLink } from '../../../components';
import useViewedListings from '../../../util/useViewedListings';
import useCardDecodedSizes from '../../../hooks/useCardDecodedSizes';

import css from './HomeListings.module.css';

// Matches the listing-card image variants requested in HomePage.duck.
const CARD_RENDER_SIZES = [
  '(max-width: 549px) 100vw',
  '(max-width: 767px) 50vw',
  '(max-width: 1023px) 33vw',
  '300px',
].join(', ');

// Responsive column count: 4 (wide) → 3 → 2 → 1. Matches the CSS breakpoints.
const columnsForWidth = width => {
  if (width >= 1280) return 4;
  if (width >= 1024) return 3;
  if (width >= 550) return 2;
  return 1;
};

// Initial column count must match SSR (4) on both server and first client
// render to avoid a hydration mismatch; the effect below corrects it post-mount.
const INITIAL_COLUMNS = 4;

// Approx. vertical gap between stacked cards (keeps the running column-height
// estimate close to the rendered CSS gap).
const VERTICAL_GAP = 26;

const sameAssignment = (a, b) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

/**
 * Homepage live-listings grid — true (height-balanced) masonry with infinite
 * scroll.
 *
 * Masonry approach: shortest-column placement. Each card is measured (its photo
 * is a fixed 4:3, content varies — e.g. on-hold badges), and the next card in
 * newest-first order drops into the currently-shortest column by running pixel
 * height. A ResizeObserver re-balances when images finish loading, when more
 * pages append, and on resize. Strict newest-first left-to-right order can
 * shift slightly between columns — accepted for even column bottoms.
 *
 * @param {Object} props
 * @param {Array} props.listings resolved listing entities (accumulated)
 * @param {Object} props.pagination Sharetribe pagination meta (page/totalPages)
 * @param {boolean} props.loadInProgress whether a page fetch is in flight
 * @param {Object} props.error storable error, if the last fetch failed
 * @param {Function} props.onLoadMore () => void — fetch + append the next page
 */
const HomeListings = props => {
  const { listings = [], pagination, loadInProgress, error, onLoadMore } = props;

  // Single localStorage read for the whole grid (empty until after mount, then
  // populated) — each card gets an isViewed boolean, not its own read.
  const viewedListings = useViewedListings();

  // 🔴 Story 12.15 (AC-5c) — ONE decoded-size map for the whole surface. Each
  // card reports its own `<img>`'s decode into it on load; nothing is fetched.
  const { decodedSizeFor, onImageDecoded } = useCardDecodedSizes();


  const sentinelRef = useRef(null);
  const [columnCount, setColumnCount] = useState(INITIAL_COLUMNS);
  // index → column assignment computed from measured heights.
  const [assignment, setAssignment] = useState([]);

  const cardEls = useRef(new Map()); // listingId → element
  const resizeObserver = useRef(null);
  const rafId = useRef(null);

  // Track the responsive column count. Clear the measured assignment on change
  // so the render falls back to a valid in-range layout until re-measured.
  useEffect(() => {
    const onResize = () => {
      setColumnCount(prev => {
        const next = columnsForWidth(window.innerWidth);
        if (next !== prev) setAssignment([]);
        return next;
      });
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Measure card heights and assign each (in order) to the shortest column.
  const measure = useCallback(() => {
    const n = listings.length;
    if (n === 0) {
      setAssignment(prev => (prev.length === 0 ? prev : []));
      return;
    }
    const colHeights = new Array(columnCount).fill(0);
    const next = new Array(n);
    for (let i = 0; i < n; i++) {
      const el = cardEls.current.get(listings[i].id.uuid);
      const h = el ? el.getBoundingClientRect().height : 0;
      let min = 0;
      for (let c = 1; c < columnCount; c++) {
        if (colHeights[c] < colHeights[min] - 0.5) min = c;
      }
      next[i] = min;
      colHeights[min] += h + VERTICAL_GAP;
    }
    setAssignment(prev => (sameAssignment(prev, next) ? prev : next));
  }, [listings, columnCount]);

  const scheduleMeasure = useCallback(() => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(measure);
  }, [measure]);

  // ResizeObserver catches async height changes (images finishing loading).
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => scheduleMeasure());
    resizeObserver.current = ro;
    cardEls.current.forEach(el => ro.observe(el));
    return () => {
      ro.disconnect();
      resizeObserver.current = null;
    };
  }, [scheduleMeasure]);

  // Re-measure after listings append or the column count changes.
  useEffect(() => {
    scheduleMeasure();
  }, [listings, columnCount, scheduleMeasure]);

  // Ref callback per card: keep the element map + ResizeObserver in sync.
  const setCardEl = id => el => {
    const map = cardEls.current;
    const ro = resizeObserver.current;
    const prev = map.get(id);
    if (prev && ro) ro.unobserve(prev);
    if (el) {
      map.set(id, el);
      if (ro) ro.observe(el);
    } else {
      map.delete(id);
    }
  };

  // Stop cleanly at the end using pagination pages (robust even though sold
  // listings are filtered client-side, which makes totalItems an over-count).
  const currentPage = pagination?.page;
  const totalPages = pagination?.totalPages;
  const hasMore = currentPage != null && totalPages != null ? currentPage < totalPages : false;

  // Infinite-scroll sentinel.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadInProgress) {
          onLoadMore();
        }
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadInProgress, onLoadMore]);

  // Distribute into columns using the measured assignment; for items not yet
  // measured (fresh appends) fall back to round-robin until the next measure.
  const columns = Array.from({ length: columnCount }, () => []);
  listings.forEach((l, i) => {
    const raw = i < assignment.length ? assignment[i] : i % columnCount;
    const col = Math.min(Math.max(raw, 0), columnCount - 1);
    columns[col].push(l);
  });

  return (
    <section className={css.section} aria-label="Live horse listings">
      <div className={css.inner}>
        <div className={css.headingRow}>
          <h2 className={css.heading}>Newest horses</h2>
          {/* Phase 7 — Filter link to the search page. */}
          <NamedLink name="SearchPage" className={css.filterLink}>
            Filter
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="7" y1="12" x2="17" y2="12" />
              <line x1="10" y1="18" x2="14" y2="18" />
            </svg>
          </NamedLink>
        </div>

        <div className={css.masonry}>
          {columns.map((col, ci) => (
            <div className={css.column} key={`col-${ci}`}>
              {col.map(l => (
                <div className={css.cardWrap} key={l.id.uuid} ref={setCardEl(l.id.uuid)}>
                  <ListingCard
                    className={css.card}
                    listing={l}
                    renderSizes={CARD_RENDER_SIZES}
                    isViewed={viewedListings.has(l.id.uuid)}
                    decodedSize={decodedSizeFor(l)}
                    onImageDecoded={onImageDecoded}
                    sourcePage="homepage"
                  />
                </div>
              ))}
            </div>
          ))}
        </div>

        {error ? <p className={css.error}>Couldn’t load more listings. Please try again.</p> : null}

        {loadInProgress ? (
          <div className={css.loadingRow}>
            <IconSpinner />
          </div>
        ) : null}

        {/* Infinite-scroll sentinel — observed only while more pages exist. */}
        {hasMore ? <div ref={sentinelRef} className={css.sentinel} aria-hidden="true" /> : null}
      </div>
    </section>
  );
};

export default HomeListings;
